import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Newspaper, Loader2, ChevronDown, ChevronUp, Sparkles, Trash2,
  ExternalLink, Eye, EyeOff, RefreshCw,
} from 'lucide-react';

const API = `${''}/api`;

export const BlogAdminPanel = () => {
  const [open, setOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [leads, setLeads] = useState(0);
  const pollRef = useRef(null);

  const loadArticles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blog/admin/articles`);
      setArticles(res.data.articles || []);
    } catch (e) {
      toast.error('Erreur chargement des articles');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blog/admin/leads`);
      setLeads(res.data.total || 0);
    } catch (e) { /* ignore */ }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blog/admin/generation-status`);
      setStatus(res.data);
      return res.data;
    } catch (e) { return null; }
  }, []);

  useEffect(() => { loadArticles(); loadStatus(); loadLeads(); }, [loadArticles, loadStatus, loadLeads]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const st = await loadStatus();
      if (st && st.running === false) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        await loadArticles();
        toast.success('Génération terminée');
      }
    }, 5000);
  }, [loadStatus, loadArticles]);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const generate = async (batch = 'pilot') => {
    try {
      const res = await axios.post(`${API}/blog/admin/generate`, { batch });
      toast.success(`Génération lancée (${res.data.total} articles) — en tâche de fond`);
      await loadStatus();
      startPolling();
    } catch (e) {
      if (e?.response?.status === 409) toast.error('Une génération est déjà en cours');
      else toast.error('Erreur lors du lancement de la génération');
    }
  };

  const togglePublish = async (a) => {
    const next = a.status === 'published' ? 'draft' : 'published';
    try {
      await axios.patch(`${API}/blog/admin/articles/${a.id}`, { status: next });
      setArticles((prev) => prev.map((x) => (x.id === a.id ? { ...x, status: next } : x)));
    } catch (e) { toast.error('Erreur mise à jour'); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement « ${a.title} » ?`)) return;
    try {
      await axios.delete(`${API}/blog/admin/articles/${a.id}`);
      setArticles((prev) => prev.filter((x) => x.id !== a.id));
      toast.success('Article supprimé');
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const running = status?.running;

  return (
    <div data-testid="blog-admin-panel" className="rounded-2xl border border-slate-700 bg-slate-900/40 p-4">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3" data-testid="blog-admin-toggle">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shrink-0">
            <Newspaper className="w-5 h-5 text-white" />
          </div>
          <div className="text-left min-w-0">
            <h3 className="text-white font-semibold flex items-center gap-2">
              Blog documentaire (SEO)
              <span className="inline-flex items-center gap-1 text-[11px] text-indigo-300 bg-indigo-500/10 px-2 py-0.5 rounded-full">
                {articles.length} article{articles.length > 1 ? 's' : ''}
              </span>
            </h3>
            <p className="text-xs text-slate-400 truncate">Fiches publiques indexées pour le référencement</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {/* Génération */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => generate('pilot')}
              disabled={running}
              data-testid="blog-generate-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm disabled:opacity-50"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {running ? 'Génération en cours…' : 'Générer le lot pilote (12)'}
            </button>
            <button
              onClick={() => generate('extended')}
              disabled={running}
              data-testid="blog-generate-extended-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" />
              Générer les fiches restantes (~88)
            </button>
            <button
              onClick={() => { loadArticles(); loadStatus(); loadLeads(); }}
              data-testid="blog-refresh-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 hover:bg-slate-700 text-white text-sm"
            >
              <RefreshCw className="w-4 h-4" /> Actualiser
            </button>
            <a
              href="/blog"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-indigo-300 hover:text-indigo-200"
            >
              Voir le blog <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <span className="text-xs text-slate-400 ml-auto" data-testid="blog-leads-count">
              Prospects captés : <span className="text-white font-medium">{leads}</span>
            </span>
          </div>

          {running && status && (
            <div className="rounded-xl bg-indigo-500/10 border border-indigo-500/20 px-4 py-2.5 text-sm text-indigo-200" data-testid="blog-gen-progress">
              Progression : {status.done}/{status.total} article(s) généré(s)…
              {status.errors?.length > 0 && <span className="text-amber-300"> · {status.errors.length} erreur(s)</span>}
            </div>
          )}

          {/* Liste */}
          {loading ? (
            <div className="flex items-center justify-center py-6 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
            </div>
          ) : articles.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">Aucun article. Lancez la génération pour créer le lot pilote.</p>
          ) : (
            <div className="space-y-2">
              {articles.map((a) => (
                <div key={a.id} data-testid={`blog-admin-row-${a.slug}`} className="flex items-center gap-3 rounded-xl bg-slate-800/40 border border-slate-800 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium truncate">{a.title}</p>
                    <p className="text-xs text-slate-500">
                      {a.category_label} · {a.views || 0} vue(s)
                      {a.status !== 'published' && <span className="text-amber-400"> · brouillon</span>}
                    </p>
                  </div>
                  <a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer" title="Voir" className="p-2 text-slate-400 hover:text-indigo-300" data-testid={`blog-view-${a.slug}`}>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={() => togglePublish(a)} title={a.status === 'published' ? 'Dépublier' : 'Publier'} className="p-2 text-slate-400 hover:text-white" data-testid={`blog-toggle-${a.slug}`}>
                    {a.status === 'published' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => remove(a)} title="Supprimer" className="p-2 text-slate-400 hover:text-red-400" data-testid={`blog-delete-${a.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogAdminPanel;
